"use client";

import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { MoreHorizontal } from "lucide-react";
import { cn } from "@newxsystems/shared";

const BreadcrumbSizeContext = React.createContext<"sm" | "md" | undefined>(undefined);

const Breadcrumb = React.forwardRef<
  HTMLElement,
  React.ComponentPropsWithoutRef<"nav"> & {
    separator?: React.ReactNode;
  }
>(({ ...props }, ref) => <nav ref={ref} aria-label="breadcrumb" {...props} />);
Breadcrumb.displayName = "Breadcrumb";

const BreadcrumbList = React.forwardRef<
  HTMLOListElement,
  React.ComponentPropsWithoutRef<"ol"> & {
    size?: "sm" | "md";
  }
>(({ className, size, ...props }, ref) => (
  <BreadcrumbSizeContext.Provider value={size}>
    <ol
      ref={ref}
      className={cn(
        `flex flex-wrap items-center break-words ${size === "sm" ? "text-[12px]/[20px] font-normal gap-1" : "text-[14px]/[24px] font-medium gap-1.5"}`,
        className,
      )}
      {...props}
    />
  </BreadcrumbSizeContext.Provider>
));
BreadcrumbList.displayName = "BreadcrumbList";

const BreadcrumbItem = React.forwardRef<
  HTMLLIElement,
  React.ComponentPropsWithoutRef<"li"> & {
    icon?: boolean;
  }
>(({ className, icon, ...props }, ref) => (
  <li ref={ref} className={cn("inline-flex items-center gap-1.5", className)} {...props} />
));
BreadcrumbItem.displayName = "BreadcrumbItem";

const BreadcrumbLink = React.forwardRef<
  HTMLAnchorElement,
  React.ComponentPropsWithoutRef<"a"> & {
    asChild?: boolean;
  }
>(({ asChild, className, ...props }, ref) => {
  const Comp = asChild ? Slot : "a";

  return (
    <Comp
      ref={ref}
      className={cn(
        "text-onSurface-400 hover:text-primary-500 rounded focus:ring-1 focus:text-onSurface-400 focus:ring-primary-500",
        className,
      )}
      {...props}
    />
  );
});
BreadcrumbLink.displayName = "BreadcrumbLink";

const BreadcrumbPage = React.forwardRef<
  HTMLSpanElement,
  React.ComponentPropsWithoutRef<"span"> & {
    icon?: boolean;
  }
>(({ className, icon, ...props }, ref) => (
  <span
    ref={ref}
    role="link"
    aria-disabled="true"
    aria-current="page"
    className={cn("text-onSurface-600", className)}
    {...props}
  />
));
BreadcrumbPage.displayName = "BreadcrumbPage";

const BreadcrumbSeparator = ({ children, className, ...props }: React.ComponentProps<"li">) => {
  const size = React.useContext(BreadcrumbSizeContext);

  return (
    <li
      role="presentation"
      aria-hidden="true"
      className={cn(
        `[&>svg]:w-3.5 [&>svg]:h-3.5 text-onSurface-400 ${size === "sm" ? "px-3" : "px-[14px]"}`,
        className,
      )}
      {...props}
    >
      {children ?? "/"}
    </li>
  );
};
BreadcrumbSeparator.displayName = "BreadcrumbSeparator";

const BreadcrumbEllipsis = ({ className, ...props }: React.ComponentProps<"span">) => (
  <span
    role="presentation"
    aria-hidden="true"
    className={cn("flex h-9 w-9 items-center justify-center", className)}
    {...props}
  >
    <MoreHorizontal className="h-4 w-4" />
    <span className="sr-only">More</span>
  </span>
);
BreadcrumbEllipsis.displayName = "BreadcrumbElipssis";

export {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
  BreadcrumbEllipsis,
};
